import React, { useState, useRef, MouseEventHandler } from 'react';
import { IntlProvider } from '@amzn/storm-ui-intl';
import { TaktIdConsumer, createStormTaktId } from '../../TaktIdContext';
import { getTranslationComponent } from '../../i18n/FileDownloadButtonTranslation/static';
import downloadFileFromHref from './downloadFileFromHref';
import logWithTakt from './logWithTakt';
import TriggerButton from './TriggerButton';
import WarningModal from './WarningModal';
import TaktWrapper from './TaktWrapper';
import generateUuid from './generateUuid';
import type { ButtonProps } from '../../Button/Button';
import type { IconDefinition } from '../../Icon/types';
import type { DataProps } from '../../types/DataProps';

type AACChromeAccessType = 'RealCustomer' | 'Impersonator';

export interface FileDownloadButtonProps extends DataProps, Omit<ButtonProps, 'type' | 'iconStart' | 'iconEnd'> {
  /**
   * Set to true to show internal user warning.
   * If not set, will default to `window.__AACChrome__.context.accessType === 'Impersonator'`.
   * The accessType will equal Impersonator when user has permission delegation permissions.
   * Otherwise it will be set to RealCustomer.
   * @defaultValue `undefined`
   */
  internalWarningVisible?: boolean;
  /**
   * Callback function for download.
   */
  onDownload: (uuid: string) => void | Promise<void>;
  /**
   * Callback function for successful download.
   */
  onDownloadSuccess?: (uuid: string) => void;
  /**
   * Callback function for failed download.
   */
  onDownloadError?: (uuid: string, error: Error) => void;
  /**
   * The URL of the file.
   */
  href?: string;
  /**
   * The file's metadata logged with Takt.
   */
  fileMetadata?: Record<string, unknown>;
  /**
   * The name of the file.
   */
  fileName: string;
  /**
   * Text displayed as Button component label.
   */
  label: string;
  /**
   * Should the label be hidden in the button.
   */
  hideLabel?: boolean;
  /**
   * The icon to display within the button.
   */
  icon?: IconDefinition | string;
  /**
   * user's locale
   * @defaultValue `"en-US"`
   */
  locale?: string;
}

const FileDownloadButton = ({
  internalWarningVisible,
  onDownload,
  onDownloadSuccess,
  onDownloadError,
  href,
  fileMetadata,
  fileName,
  onClick,
  label,
  hideLabel,
  icon,
  taktId,
  taktValue,
  locale,
  ...restProps
}: FileDownloadButtonProps) => {
  const taktFallbackId = createStormTaktId('file-download-button') as string;
  const internalTaktId = restProps['data-takt-id'] ?? taktId ?? taktFallbackId;
  const translations = getTranslationComponent(locale);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const buttonRef = useRef();

  /*
   * accessType will equal Impersonator when user has permission delegation permissions
   * (advertiser_summary_view and/or WA_MA_USER but not advertiser_campaign_edit permission).
   * Otherwise it will be set to RealCustomer.
   * Related CR: https://code.amazon.com/reviews/CR-225251823
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  // eslint-disable-next-line no-underscore-dangle
  const accessType: undefined | AACChromeAccessType = window?.__AACChrome__?.context?.accessType;
  const isInternalUser = internalWarningVisible ?? accessType === 'Impersonator';

  const handleClick: MouseEventHandler<HTMLElement & HTMLButtonElement & HTMLAnchorElement> = async event => {
    const uuid = generateUuid();
    const taktDetails = { fileName, uuid, ...(fileMetadata || {}) };
    try {
      onClick?.(event);
      if (href) {
        downloadFileFromHref(fileName, href, `${internalTaktId}-temporary-a`);
      }
      logWithTakt('storm_ui_file_download_button_download_initial', taktDetails);
      await onDownload(uuid);
      logWithTakt('storm_ui_file_download_button_download_success', taktDetails);
      onDownloadSuccess?.(uuid);
    } catch (error) {
      logWithTakt('storm_ui_file_download_button_download_error', taktDetails);
      onDownloadError?.(
        uuid,
        error instanceof Error
          ? error
          : new Error('storm_ui_file_download_button_download_error'),
      );
    }
  };

  return (
    <TaktWrapper
      taktId={internalTaktId}
    >
      {isInternalUser ? (
        <IntlProvider
          resetKey={locale}
          translations={translations}
        >
          <TaktIdConsumer
            taktId={internalTaktId}
            taktValue={taktValue}
          >
            {({ getDataTaktAttributes }) => (
              <>
                <TriggerButton
                  {...restProps}
                  {...getDataTaktAttributes({ taktIdSuffix: 'internal-warning-trigger-button' })}
                  buttonRef={buttonRef}
                  hideLabel={hideLabel}
                  icon={icon}
                  label={label}
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                />
                <WarningModal
                  cancelButtonProps={{
                    ...getDataTaktAttributes({ taktIdSuffix: 'internal-warning-cancel-button' }),
                  }}
                  confirmButtonProps={{
                    ...restProps,
                    ...getDataTaktAttributes({ taktIdSuffix: 'internal-warning-confirm-button' }),
                  }}
                  isOpen={isModalOpen}
                  label={label}
                  onCancel={() => {
                    setIsModalOpen(false);
                  }}
                  onConfirm={event => {
                    setIsModalOpen(false);
                    handleClick(event);
                  }}
                  toggleEl={buttonRef.current}
                />
              </>
            )}
          </TaktIdConsumer>
        </IntlProvider>
      ) : (
        <TriggerButton
          {...restProps}
          taktId={taktId}
          taktValue={taktValue}
          taktFallbackId={taktFallbackId}
          hideLabel={hideLabel}
          icon={icon}
          label={label}
          onClick={handleClick}
        />
      )}
    </TaktWrapper>
  );
};

export default FileDownloadButton;
